import { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useRouteContext } from "@tanstack/react-router";
import Spinner from "react-bootstrap/Spinner";

import WizardPopup from "@/components/shared/WizardPopup";
import {
  StaffWizardSearchResults,
  StaffSearchResultsRef,
} from "./StaffWizardSearchResults";
import { STFFWizardForm } from "./forms/STFFWizardForm";
import { SASSWizardForm } from "./forms/SASSWizardForm";
import { SCLRWizardForm } from "./forms/SCLRWizardForm";
import { StaffReviewSteps } from "./forms/StaffReviewSteps";
import { StepIndicator } from "@/components/shared/StepIndicator";
import { SkipOptionalStep } from "@/components/shared/OptionalStepSkipCheckbox";
import { StaffWizardSearchForm, StaffSearchFormRef } from "./StaffWizardSearch";
import { useLeaData } from "@/queries";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import LoaderButton from "@/components/shared/LoaderButton";
import { useStaffSubmission } from "./hooks/useStaffSubmission";
import {
  useAddStaffWizard,
  MAX_ASSIGNMENTS,
  MAX_CLASSROOMS,
} from "./hooks/useAddStaffWizard";
import { FaPlus } from "react-icons/fa";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { focusFirstInvalidField } from "@/utils/forms";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type AddStaffWizardProps = {
  show: boolean;
  onClose: () => void;
  preschools: PreschoolSummary[];
};

export function AddStaffWizard({
  show,
  onClose,
  preschools,
}: AddStaffWizardProps) {
  const {
    currentStep,
    completedSteps,
    searchResults,
    showResults,
    searchedData,
    isNewRecord,
    lockedFields,
    form,
    assignmentForms,
    activeAssignmentCount,
    classroomForms,
    activeClassroomCount,
    canCreateNew,
    searchError,
    searchCriteria,
    skipAssignment,
    skipClassroom,
    handleSearch,
    handleSelectStaff,
    handleCreateNew,
    resetWizard,
    setCurrentStep,
    setCompletedSteps,
    setSearchError,
    clearSearchError,
    handleSkipAssignment,
    handleSkipClassroom,
    addAssignment,
    removeAssignment,
    addClassroom,
    removeClassroom,
  } = useAddStaffWizard();

  const leaContext = useRouteContext({ from: "/$leaid" });
  const coreData = leaContext.coreData;
  const esiIdPath = leaContext.currentUserOrg?.esiIdPath ?? "";
  const result = useLeaData(esiIdPath);
  const leaData = result.data;

  // Check edit permissions for optional record types
  const canEditAssignment = leaContext.hasRole("SASSEdit");
  const canEditClassroom = leaContext.hasRole("SCLREdit");

  const { addPendingFileSubmission } = useContext(FileSubmissionsContext);

  const [shouldClose, setShouldClose] = useState(false);

  const [stffError, setStffError] = useState<string | null>(null);

  const searchFormRef = useRef<StaffSearchFormRef>(null);
  const searchResultsRef = useRef<StaffSearchResultsRef>(null);

  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showBackToSearchConfirm, setShowBackToSearchConfirm] = useState(false);
  const [removeConfirmation, setRemoveConfirmation] = useState<{
    label: string;
    onConfirm: () => void;
    fallbackFocusRef?: React.RefObject<HTMLElement | null>;
  } | null>(null);

  const requestRemoveConfirmation = (
    label: string,
    onConfirm: () => void,
    fallbackFocusRef?: React.RefObject<HTMLElement | null>
  ) => {
    setRemoveConfirmation({ label, onConfirm, fallbackFocusRef });
  };

  // Durable "Add Another..." targets to refocus after a confirmed removal
  // unmounts the button that triggered the confirmation.
  const addAssignmentRef = useRef<HTMLDivElement>(null);
  const addClassroomRef = useRef<HTMLDivElement>(null);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const isInitialStepRender = useRef(true);
  const wizardLoadingRef = useRef<HTMLDivElement>(null);

  const handleCancelRemove = () => setRemoveConfirmation(null);

  const handleConfirmRemove = () => {
    removeConfirmation?.onConfirm();
    setRemoveConfirmation(null);
  };

  // Auto-skip optional steps when user doesn't have edit permission
  useEffect(() => {
    if (!canEditAssignment && !skipAssignment) {
      handleSkipAssignment(true);
    }
    if (!canEditClassroom && !skipClassroom) {
      handleSkipClassroom(true);
    }
  }, [
    canEditAssignment,
    canEditClassroom,
    skipAssignment,
    skipClassroom,
    handleSkipAssignment,
    handleSkipClassroom,
  ]);

  // Build available steps based on permissions
  const availableSteps = useMemo(() => {
    const steps: Array<{
      num: number;
      label: string;
      subtitle?: string;
      displayNum: number;
    }> = [
      { num: 1, label: "Search", displayNum: 1 },
      { num: 2, label: "Staff Demographics", displayNum: 2 },
    ];

    let displayNum = 3;

    if (canEditAssignment) {
      steps.push({
        num: 3,
        label: "Staff Assignment",
        subtitle: "(Optional)",
        displayNum: displayNum++,
      });
    }

    if (canEditClassroom) {
      steps.push({
        num: 4,
        label: "Staff Classroom",
        subtitle: "(Optional)",
        displayNum: displayNum++,
      });
    }

    steps.push({ num: 5, label: "Review & Submit", displayNum: displayNum });

    return steps;
  }, [canEditAssignment, canEditClassroom]);

  const sassCodes = skipAssignment
    ? []
    : (assignmentForms
        .slice(0, activeAssignmentCount)
        .map((f) => f.getValues().PreschoolCode)
        .filter(Boolean) as string[]);

  const sassPreschoolCodes =
    sassCodes.length > 0 ? Array.from(new Set(sassCodes)) : null;

  // Helper to get next available step
  const getNextAvailableStep = (current: number): number => {
    const stepNumbers = availableSteps.map((s) => s.num);
    const currentIndex = stepNumbers.indexOf(current);
    if (currentIndex === -1 || currentIndex === stepNumbers.length - 1) {
      return current;
    }
    return stepNumbers[currentIndex + 1];
  };

  // Helper to get previous available step
  const getPrevAvailableStep = (current: number): number => {
    const stepNumbers = availableSteps.map((s) => s.num);
    const currentIndex = stepNumbers.indexOf(current);
    if (currentIndex <= 0) {
      return current;
    }
    return stepNumbers[currentIndex - 1];
  };

  const validateActiveAssignmentForms = async () => {
    const results = await Promise.all(
      assignmentForms.slice(0, activeAssignmentCount).map((f) => f.trigger())
    );
    return results.every(Boolean);
  };

  const validateActiveClassroomForms = async () => {
    const results = await Promise.all(
      classroomForms.slice(0, activeClassroomCount).map((f) => f.trigger())
    );
    return results.every(Boolean);
  };

  const handleStffPosted = () => {
    setStffError(null);
    setShouldClose(true);
  };

  const handleSubmissionComplete = () => {};

  useEffect(() => {
    if (shouldClose) {
      setShouldClose(false);
      onClose();

      resetWizard();
    }
  }, [shouldClose, onClose, resetWizard]);

  const handleStffFailed = (errors: any[]) => {
    setCurrentStep(2);

    const errorMessage =
      errors.length > 0
        ? errors[0]?.Message ||
          errors[0]?.message ||
          "Validation error occurred."
        : "STFF validation failed. Please check your data and try again.";

    setStffError(errorMessage);
  };

  useEffect(() => {
    if (currentStep !== 2 && stffError) {
      setStffError(null);
    }
  }, [currentStep, stffError]);

  const { submitStaff, isSubmitting, phase } = useStaffSubmission(
    {
      stffForm: form,
      assignmentForms,
      classroomForms,
    },
    {
      skipAssignment,
      skipClassroom,
      activeAssignmentCount,
      activeClassroomCount,
    },
    leaData,
    esiIdPath,
    {
      onStffPosted: handleStffPosted,
      onComplete: handleSubmissionComplete,
      onStffFailed: handleStffFailed,
      addPendingFileSubmission,
    }
  );

  const showWizardLoading =
    isSubmitting &&
    (phase === "submitting-stff" ||
      phase === "waiting-for-stff" ||
      phase === "submitting-related");

  useEffect(() => {
    if (showWizardLoading) {
      wizardLoadingRef.current?.focus();
    }
  }, [showWizardLoading]);

  useEffect(() => {
    const modalBody = document.querySelector(".add-staff-wizard .modal-body");
    if (modalBody) {
      modalBody.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep, showResults]);

  useEffect(() => {
    if (isInitialStepRender.current) {
      isInitialStepRender.current = false;
      return;
    }
    stepHeadingRef.current?.focus();
  }, [currentStep, showResults]);

  const handleCloseAttempt = () => {
    if (currentStep >= 2) {
      setShowExitConfirm(true);
    } else {
      handleCancel();
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirm(false);
    handleCancel();
  };

  const handleCancelExit = () => {
    setShowExitConfirm(false);
  };

  const handleCancel = () => {
    resetWizard();
    onClose();
  };

  const handleBackToSearch = () => {
    resetWizard();
  };

  const handleConfirmBackToSearch = () => {
    setShowBackToSearchConfirm(false);
    resetWizard();
  };

  const handleCancelBackToSearch = () => {
    setShowBackToSearchConfirm(false);
  };


  const prepopulateStep2Dependents = () => {
    const stffValues = form.getValues();

    // Prepopulate assignment forms if user has permission
    if (canEditAssignment) {
      for (let i = 0; i < activeAssignmentCount; i++) {
        assignmentForms[i].setValue(
          "ProgramYear",
          stffValues.ProgramYear || ""
        );
        assignmentForms[i].setValue(
          "LocalStaffId",
          stffValues.LocalStaffId || ""
        );
      }
    }

    // Prepopulate classroom forms from STFF (ProgramYear and LocalStaffId only;
    // Preschool stays empty per the SCLR story - user picks per record)
    if (canEditClassroom) {
      for (let i = 0; i < activeClassroomCount; i++) {
        classroomForms[i].setValue(
          "ProgramYear",
          stffValues.ProgramYear || ""
        );
        classroomForms[i].setValue(
          "LocalStaffId",
          stffValues.LocalStaffId || ""
        );
      }
    }
  };

  // Custom handleNext that skips unavailable steps and prepopulates from STFF
  const handleNext = async () => {
    let isValid = true;

    if (currentStep === 2) {
      isValid = await form.trigger();

      if (isValid) {
        prepopulateStep2Dependents();
      }
    } else if (currentStep === 3 && canEditAssignment) {
      if (skipAssignment) {
        const stffValues = form.getValues();
        assignmentForms.forEach((f) =>
          f.reset({
            ...f.getValues(),
            PreschoolCode: "",
            ProgramYear: stffValues.ProgramYear || "",
            LocalStaffId: stffValues.LocalStaffId || "",
          })
        );
        isValid = true;
      } else {
        isValid = await validateActiveAssignmentForms();
      }
    } else if (currentStep === 4 && canEditClassroom) {
      if (skipClassroom) {
        const stffValues = form.getValues();
        classroomForms.forEach((f) =>
          f.reset({
            ...f.getValues(),
            ProgramYear: stffValues.ProgramYear || "",
            LocalStaffId: stffValues.LocalStaffId || "",
          })
        );
        isValid = true;
      } else {
        isValid = await validateActiveClassroomForms();
      }
    }

    if (isValid) {
      const nextStep = getNextAvailableStep(currentStep);
      setCurrentStep(nextStep);

      setCompletedSteps((prev) => {
        const newCompleted = new Set([...prev, currentStep, nextStep]);
        return Array.from(newCompleted).sort((a, b) => a - b);
      });
    } else {
      focusFirstInvalidField(".add-staff-wizard .modal-body");
    }
  };

  // Custom handleBack that skips unavailable steps
  const handleBack = () => {
    if (currentStep === 2) {
      setShowBackToSearchConfirm(true);
      return;
    }
    const prevStep = getPrevAvailableStep(currentStep);
    setCurrentStep(prevStep);
  };

  const isStepActionable = (stepNum: number) =>
    stepNum <= currentStep ||
    (stepNum === getNextAvailableStep(currentStep) &&
      completedSteps.includes(currentStep));

  const handleStepClick = async (stepNum: number) => {
    if (currentStep === 1 && stepNum === 2) {
      return;
    }

    // Only allow clicking on available steps
    const isAvailable = availableSteps.some((s) => s.num === stepNum);
    if (!isAvailable) {
      return;
    }

    if (stepNum === 1 && currentStep >= 2) {
      setShowBackToSearchConfirm(true);
      return;
    }

    if (isStepActionable(stepNum)) {
      let isValid = true;

      if (stepNum > currentStep) {
        if (currentStep === 2) {
          isValid = await form.trigger();
          if (isValid) {
            prepopulateStep2Dependents();
          }
        } else if (currentStep === 3 && !skipAssignment && canEditAssignment) {
          isValid = await validateActiveAssignmentForms();
        } else if (currentStep === 4 && !skipClassroom && canEditClassroom) {
          isValid = await validateActiveClassroomForms();
        }
      }

      if (isValid) {
        setCurrentStep(stepNum);
        if (!completedSteps.includes(stepNum)) {
          setCompletedSteps((prev) => {
            return [...prev, stepNum].sort((a, b) => a - b);
          });
        }
      } else {
        focusFirstInvalidField(".add-staff-wizard .modal-body");
      }
    }
  };

  const handleSubmit = () => {
    setStffError(null);
    submitStaff(isNewRecord);
  };

  const renderFooter = () => {
    if (currentStep === 1) {
      if (showResults) {
        return (
          <div className="d-flex justify-content-end gap-2 w-100">
            <button
              className="btn btn-outline-secondary"
              onClick={() => searchResultsRef.current?.handleBack()}
              disabled={isSubmitting}
            >
              Previous
            </button>
          </div>
        );
      }

      return (
        <div className="d-flex justify-content-end gap-2 w-100">
          <button
            className="btn btn-outline-secondary"
            onClick={() => searchFormRef.current?.handleCancel()}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            style={{ backgroundColor: "#1e5a78", borderColor: "#1e5a78" }}
            onClick={() => searchFormRef.current?.handleSearch()}
            disabled={isSubmitting}
          >
            Search
          </button>
        </div>
      );
    }

    return (
      <div className="d-flex justify-content-end gap-2 w-100">
        <button
          className="btn btn-outline-secondary"
          onClick={handleBack}
          disabled={isSubmitting}
        >
          Previous
        </button>

        {currentStep === 5 ? (
          <LoaderButton
            variant="primary"
            style={{ backgroundColor: "#1e5a78", borderColor: "#1e5a78" }}
            onClick={handleSubmit}
            loading={isSubmitting && !showWizardLoading}
            loadingText="Submitting..."
            disabled={isSubmitting}
          >
            Submit
          </LoaderButton>
        ) : (
          <button
            className="btn btn-primary"
            style={{ backgroundColor: "#1e5a78", borderColor: "#1e5a78" }}
            onClick={handleNext}
            disabled={isSubmitting}
          >
            Next
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <WizardPopup
        show={show}
        header={
          <>
            <i className="bi bi-person-plus"></i>Add Staff
          </>
        }
        size="xl"
        onClose={handleCloseAttempt}
        enforceFocus={
          !showExitConfirm && !showBackToSearchConfirm && !removeConfirmation
        }
        hasButton={false}
        className="add-staff-wizard"
        backdrop="static"
        footer={renderFooter()}
      >
        {showWizardLoading ? (
          <div
            role="alert"
            tabIndex={-1}
            ref={wizardLoadingRef}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "calc(100vh - 200px)",
              maxHeight: "calc(100vh - 200px)",
              backgroundColor: "white",
            }}
          >
            <Spinner
              animation="border"
              variant="primary"
              style={{ width: "3rem", height: "3rem" }}
            />
            <p
              style={{
                marginTop: "16px",
                fontSize: "16px",
                color: "#1e5a78",
                fontWeight: 500,
              }}
            >
              Waiting for STFF record to be processed...
            </p>
            <p
              style={{
                marginTop: "8px",
                fontSize: "14px",
                color: "#666",
              }}
            >
              Please do not close this window.
            </p>
          </div>
        ) : (
          <div style={{ background: "white", padding: "30px 0px 80px" }}>
            <StepIndicator
              steps={availableSteps}
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={handleStepClick}
              isStepActionable={isStepActionable}
              skippedSteps={[
                skipAssignment && canEditAssignment ? 3 : null,
                skipClassroom && canEditClassroom ? 4 : null,
              ].filter((s): s is number => s !== null)}
            />

            <hr
              style={{
                margin: "24px 0",
                border: "none",
                borderTop: "1px solid #ccc",
              }}
            />

            <h2 ref={stepHeadingRef} tabIndex={-1} className="visually-hidden">
              {`Step ${
                availableSteps.find((s) => s.num === currentStep)
                  ?.displayNum ?? currentStep
              }: ${
                availableSteps.find((s) => s.num === currentStep)?.label ?? ""
              }`}
            </h2>

            {currentStep === 1 && !showResults && (
              <StaffWizardSearchForm
                ref={searchFormRef}
                preschools={preschools}
                disabled={false}
                onSearch={handleSearch}
                onCancel={handleCancel}
                searchError={searchError}
                onClearSearchError={clearSearchError}
                searchCriteria={searchCriteria}
              />
            )}

            {currentStep === 1 && showResults && (
              <StaffWizardSearchResults
                ref={searchResultsRef}
                searchResults={searchResults}
                searchedName={searchedData.name}
                onSelectStaff={handleSelectStaff}
                onCreateNew={handleCreateNew}
                onBack={handleBackToSearch}
                canCreateNew={canCreateNew}
                preschools={preschools}
              />
            )}

            {currentStep === 2 && (
              <>
                {stffError && (
                  <div
                    style={{
                      padding: "12px 16px",
                      background: "#f8d7da",
                      border: "1px solid #f5c2c7",
                      borderRadius: "4px",
                      marginBottom: "16px",
                      marginLeft: "24px",
                      marginRight: "24px",
                      fontSize: "14px",
                      color: "#842029",
                    }}
                  >
                    <strong>STFF Validation Failed:</strong> {stffError}
                  </div>
                )}
                <STFFWizardForm
                  form={form}
                  isNewRecord={isNewRecord}
                  lockedFields={lockedFields}
                />
              </>
            )}

            {currentStep === 3 && canEditAssignment && (
              <SkipOptionalStep
                checked={skipAssignment}
                onChange={handleSkipAssignment}
              >
                <>
                  {assignmentForms
                    .slice(0, activeAssignmentCount)
                    .map((f, i) => (
                      <SASSWizardForm
                        key={i}
                        form={f}
                        assignmentNumber={i + 1}
                        onRemove={
                          i === 0
                            ? undefined
                            : () =>
                                requestRemoveConfirmation(
                                  `Staff Assignment ${i + 1}`,
                                  () => removeAssignment(i),
                                  addAssignmentRef
                                )
                        }
                      />
                    ))}

                  {activeAssignmentCount < MAX_ASSIGNMENTS && (
                    <div className="ps-3 pe-3">
                      <div
                        ref={addAssignmentRef}
                        className="wizard-add-family-btn"
                        role="button"
                        tabIndex={0}
                        onClick={addAssignment}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            addAssignment();
                          }
                        }}
                      >
                        <FaPlus className="me-2 wizard-add-family-icon" />
                        <span className="wizard-add-family-text">
                          Add Another Staff Assignment (Optional)
                        </span>
                      </div>
                    </div>
                  )}
                </>
              </SkipOptionalStep>
            )}

            {currentStep === 4 && canEditClassroom && (
              <SkipOptionalStep
                checked={skipClassroom}
                onChange={handleSkipClassroom}
              >
                <>
                  {classroomForms.slice(0, activeClassroomCount).map((f, i) => (
                    <SCLRWizardForm
                      key={i}
                      form={f}
                      classroomNumber={i + 1}
                      onRemove={
                        i === 0
                          ? undefined
                          : () =>
                              requestRemoveConfirmation(
                                `Staff Classroom ${i + 1}`,
                                () => removeClassroom(i),
                                addClassroomRef
                              )
                      }
                      allowedPreschoolCodes={sassPreschoolCodes}
                    />
                  ))}

                  {activeClassroomCount < MAX_CLASSROOMS && (
                    <div className="ps-3 pe-3">
                      <div
                        ref={addClassroomRef}
                        className="wizard-add-family-btn"
                        role="button"
                        tabIndex={0}
                        onClick={addClassroom}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            addClassroom();
                          }
                        }}
                      >
                        <FaPlus className="me-2 wizard-add-family-icon" />
                        <span className="wizard-add-family-text">
                          Add Another Staff Classroom (Optional)
                        </span>
                      </div>
                    </div>
                  )}
                </>
              </SkipOptionalStep>
            )}

            {currentStep === 5 && (
              <StaffReviewSteps
                form={form}
                assignmentForms={assignmentForms}
                activeAssignmentCount={activeAssignmentCount}
                classroomForms={classroomForms}
                activeClassroomCount={activeClassroomCount}
                preschools={preschools}
                coreData={coreData}
                skipAssignment={skipAssignment}
                skipClassroom={skipClassroom}
                onEditStep={setCurrentStep}
                canEditAssignment={canEditAssignment}
                canEditClassroom={canEditClassroom}
              />
            )}
          </div>
        )}
      </WizardPopup>

      {showExitConfirm && (
        <ExitConfirmModal
          onCancel={handleCancelExit}
          onConfirm={handleConfirmExit}
        />
      )}

      {showBackToSearchConfirm && (
        <BackToSearchConfirmModal
          onCancel={handleCancelBackToSearch}
          onConfirm={handleConfirmBackToSearch}
        />
      )}

      {removeConfirmation && (
        <ConfirmModal
          title={`Remove ${removeConfirmation.label}?`}
          message={`Are you sure you want to remove this ${removeConfirmation.label.toLowerCase()}? Any information entered will be lost.`}
          confirmText="Remove"
          cancelText="Cancel"
          onCancel={handleCancelRemove}
          onConfirm={handleConfirmRemove}
          fallbackFocusRef={removeConfirmation.fallbackFocusRef}
        />
      )}
    </>
  );
}

function ExitConfirmModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
      return;
    }

    if (e.key === "Tab") {
      if (e.shiftKey && document.activeElement === cancelRef.current) {
        e.preventDefault();
        confirmRef.current?.focus();
      } else if (!e.shiftKey && document.activeElement === confirmRef.current) {
        e.preventDefault();
        cancelRef.current?.focus();
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-confirm-title"
      aria-describedby="exit-confirm-message"
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: "white",
          padding: "32px 40px",
          borderRadius: "8px",
          maxWidth: "480px",
          width: "90%",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
      >
        <h5
          id="exit-confirm-title"
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#333",
            marginBottom: "12px",
            textAlign: "left",
            margin: "0 0 12px 0",
          }}
        >
          Are you sure you want to exit?
        </h5>
        <p
          id="exit-confirm-message"
          style={{
            fontSize: "14px",
            color: "#666",
            marginBottom: "24px",
            lineHeight: "1.5",
            textAlign: "left",
            margin: "0 0 24px 0",
          }}
        >
          All unsaved data will be lost. This action cannot be undone.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <button
            ref={cancelRef}
            onClick={onCancel}
            style={{
              padding: "8px 20px",
              background: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              color: "#333",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Continue Editing
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            style={{
              padding: "8px 20px",
              background: "#1e5a78",
              border: "none",
              borderRadius: "4px",
              color: "white",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Exit
          </button>
        </div>
      </div>
    </div>
  );
}

function BackToSearchConfirmModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
      return;
    }

    if (e.key === "Tab") {
      if (e.shiftKey && document.activeElement === cancelRef.current) {
        e.preventDefault();
        confirmRef.current?.focus();
      } else if (!e.shiftKey && document.activeElement === confirmRef.current) {
        e.preventDefault();
        cancelRef.current?.focus();
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="back-to-search-title"
      aria-describedby="back-to-search-message"
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: "white",
          padding: "32px 40px",
          borderRadius: "8px",
          maxWidth: "480px",
          width: "90%",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
      >
        <h5
          id="back-to-search-title"
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#333",
            marginBottom: "12px",
            textAlign: "left",
            margin: "0 0 12px 0",
          }}
        >
          Return to Search?
        </h5>
        <p
          id="back-to-search-message"
          style={{
            fontSize: "14px",
            color: "#666",
            marginBottom: "24px",
            lineHeight: "1.5",
            textAlign: "left",
            margin: "0 0 24px 0",
          }}
        >
          Going back to the search screen will clear all entered data across all
          steps. This action cannot be undone.
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <button
            ref={cancelRef}
            onClick={onCancel}
            style={{
              padding: "8px 20px",
              background: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              color: "#333",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Continue Editing
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            style={{
              padding: "8px 20px",
              background: "#1e5a78",
              border: "none",
              borderRadius: "4px",
              color: "white",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Return to Search
          </button>
        </div>
      </div>
    </div>
  );
}
